import { cn } from '@/lib/utils';

/**
 * A choice between two or three named options, all of them visible.
 *
 * The console had two of these written out by hand -- the Tasks page's range and
 * the density switch on `/me` -- plus one icon toggle in the rail standing in
 * for a third. An icon that changes meaning depending on state is a control
 * nobody can read at rest: the density button is the same shape in both modes
 * and its indigo tint could as easily mean "active" as "compact".
 *
 * So the settings page names every option and presses the current one, which is
 * the pattern that needs no legend.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (next: T) => void;
  /** Announced to a screen reader; the visible label is the row around it. */
  label: string;
  className?: string;
}) {
  return (
    <fieldset
      className={cn(
        'border-line-2 flex shrink-0 items-center gap-px rounded-full border p-px',
        className,
      )}
    >
      <legend className="sr-only">{label}</legend>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={cn(
            'rounded-full px-3 py-1 text-xs transition-colors duration-100',
            value === option.value
              ? 'bg-raised text-foreground font-medium'
              : 'text-fg-3 hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}

/** One labelled setting: a name, a sentence, and the control on the right. */
export function SettingRow({
  title,
  hint,
  control,
}: {
  title: string;
  hint?: React.ReactNode;
  control: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-gutter py-3">
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium">{title}</p>
        {hint ? <p className="text-fg-3 mt-0.5 text-xs">{hint}</p> : null}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}
